import { useExtensionUtil } from '@/hooks/useExtensionUtil';
import { useSessionsStore } from '@/stores/sessions-store';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { SessionContextRules, SessionDomainRule } from '@/types/session-context';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Sparkles, Trash2 } from 'lucide-react';
import api from '@/api';
import axios from 'axios';
import { notifications } from '@/utils/notifications';

export type SessionContextRulesFormValue = SessionContextRules;

type TabSample = { title: string; host: string };

interface SessionContextRulesFieldsProps {
  value: SessionContextRulesFormValue;
  onChange: (next: SessionContextRulesFormValue) => void;
  syncScope?: 'workspace' | 'local';
  domainConflicts?: Array<{ host: string; otherSessionName: string }>;
  sessionId?: string;
  sessionName?: string;
}

export function SessionContextRulesFields({
  value,
  onChange,
  syncScope,
  domainConflicts = [],
  sessionId,
  sessionName = '',
}: SessionContextRulesFieldsProps) {
  const { t } = useTranslation('common');
  const { sendMessage } = useExtensionUtil();
  const { sessionUpdate } = useSessionsStore();
  const pendingImportRef = useRef(false);
  const pendingAiFillRef = useRef(false);
  const valueRef = useRef(value);
  const [aiLoading, setAiLoading] = useState(false);

  valueRef.current = value;

  const applyAiSuggestions = useCallback(
    async (name: string, tabs: TabSample[]) => {
      if (!tabs.length) {
        notifications.warning(t('sessions.contextRules.aiNoTabs'));
        return;
      }
      setAiLoading(true);
      try {
        const response = await api.post<{
          status: string;
          contextRules?: {
            description?: string;
            keywords?: string[];
            domains?: SessionDomainRule[];
          };
          message?: string;
        }>('/ai/session/suggest-context-rules', {
          sessionName: name.trim() || 'Session',
          tabs,
        });
        if (response.data.status !== 'success' || !response.data.contextRules) {
          throw new Error(response.data.message ?? t('sessions.contextRules.aiFailed'));
        }
        const suggested = response.data.contextRules;
        const current = valueRef.current;
        onChange({
          ...current,
          description: suggested.description ?? current.description,
          keywords: suggested.keywords?.length ? suggested.keywords : current.keywords,
          domains: suggested.domains?.length ? suggested.domains : current.domains,
        });
        notifications.success(t('sessions.contextRules.aiSuccess'));
      } catch (err) {
        let message = t('sessions.contextRules.aiFailed');
        if (axios.isAxiosError(err)) {
          message = err.response?.data?.message ?? message;
        } else if (err instanceof Error && err.message) {
          message = err.message;
        }
        notifications.error(message);
      } finally {
        setAiLoading(false);
      }
    },
    [onChange, t]
  );

  useEffect(() => {
    const update = sessionUpdate as Record<string, unknown> | undefined;
    if (!update?.type || update.sid !== sessionId) return;

    if (pendingImportRef.current && update.type === 'sessionTabDomains') {
      pendingImportRef.current = false;
      const hosts = (update.hosts as string[] | undefined) ?? [];
      if (!hosts.length) return;
      const current = valueRef.current;
      const existing = new Set((current.domains ?? []).map(d => d.host.toLowerCase()));
      const merged = [...(current.domains ?? [])];
      for (const host of hosts) {
        if (!existing.has(host.toLowerCase())) {
          merged.push({ host, includeSubpaths: true });
        }
      }
      onChange({ ...current, domains: merged });
      return;
    }

    if (pendingAiFillRef.current && update.type === 'sessionContextTabSamples') {
      pendingAiFillRef.current = false;
      const tabs = (update.tabs as TabSample[] | undefined) ?? [];
      const name = String(update.sessionName ?? sessionName);
      void applyAiSuggestions(name, tabs);
    }
  }, [sessionUpdate, sessionId, sessionName, onChange, applyAiSuggestions]);

  const updateDomain = (index: number, patch: Partial<SessionDomainRule>) => {
    const domains = [...(value.domains ?? [])];
    domains[index] = { ...domains[index], ...patch };
    onChange({ ...value, domains });
  };

  const addDomain = () => {
    onChange({
      ...value,
      domains: [...(value.domains ?? []), { host: '', includeSubpaths: true }],
    });
  };

  const removeDomain = (index: number) => {
    const domains = [...(value.domains ?? [])];
    domains.splice(index, 1);
    onChange({ ...value, domains });
  };

  const keywordsText = (value.keywords ?? []).join(', ');
  const setKeywords = (text: string) => {
    const keywords = text
      .split(',')
      .map(k => k.trim())
      .filter(Boolean);
    onChange({ ...value, keywords });
  };

  const importFromOpenTabs = () => {
    if (!sessionId) return;
    pendingImportRef.current = true;
    sendMessage('tabMgm', { action: 'sessionTabDomains', sid: sessionId });
  };

  const fillWithAi = () => {
    if (!sessionId || aiLoading) return;
    pendingAiFillRef.current = true;
    sendMessage('tabMgm', { action: 'sessionContextTabSamples', sid: sessionId });
  };

  return (
    <div className="space-y-4 border-t border-border pt-4 mt-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">
          {t('sessions.contextRules.title')}
        </h3>
        <div className="flex flex-col items-end gap-1">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {sessionId && (
              <button
                type="button"
                disabled={aiLoading}
                className="inline-flex items-center gap-1 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
                onClick={fillWithAi}
              >
                {aiLoading ? (
                  <Loader2 size={14} className="shrink-0 animate-spin" aria-hidden />
                ) : (
                  <Sparkles size={14} className="shrink-0" aria-hidden />
                )}
                {aiLoading
                  ? t('sessions.contextRules.aiFilling')
                  : t('sessions.contextRules.fillWithAi')}
              </button>
            )}
            {syncScope && (
              <span className="text-xs text-muted-foreground">
                {syncScope === 'workspace'
                  ? t('sessions.contextRules.syncsAcrossDevices')
                  : t('sessions.contextRules.thisDeviceOnly')}
              </span>
            )}
          </div>
          {sessionId && (
            <p className="text-[10px] text-muted-foreground text-right max-w-[220px]">
              {t('sessions.contextRules.aiPrivacy')}
            </p>
          )}
        </div>
      </div>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-muted-foreground">
          {t('sessions.contextRules.description')}
        </span>
        <textarea
          className="w-full min-h-[72px] rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={value.description ?? ''}
          onChange={e => onChange({ ...value, description: e.target.value })}
          placeholder={t('sessions.contextRules.descriptionPlaceholder')}
        />
      </label>

      <label className="block space-y-1">
        <span className="text-xs font-medium text-muted-foreground">
          {t('sessions.contextRules.keywords')}
        </span>
        <input
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={keywordsText}
          onChange={e => setKeywords(e.target.value)}
          placeholder={t('sessions.contextRules.keywordsPlaceholder')}
        />
      </label>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-xs font-medium text-muted-foreground shrink-0">
            {t('sessions.contextRules.domains')}
          </span>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-primary leading-none"
            onClick={addDomain}
          >
            <Plus size={14} className="shrink-0" aria-hidden />
            {t('sessions.contextRules.addDomain')}
          </button>
          {sessionId && (
            <button
              type="button"
              className="inline-flex items-center text-xs font-medium text-muted-foreground hover:text-foreground leading-none sm:ml-auto"
              onClick={importFromOpenTabs}
            >
              {t('sessions.contextRules.importFromTabs')}
            </button>
          )}
        </div>
        {(value.domains ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground px-1">
            {t('sessions.contextRules.domainsEmptyHint')}
          </p>
        ) : (
          <div className="max-h-[min(240px,40vh)] overflow-y-auto overflow-x-hidden rounded-md border border-border pr-1 custom-scrollbar">
            <div className="space-y-2 p-1">
              {(value.domains ?? []).map((domain, index) => (
                <div key={index} className="flex flex-wrap items-center gap-2">
                  <input
                    className="flex-1 min-w-[140px] rounded-md border border-border bg-background px-3 py-2 text-sm"
                    value={domain.host}
                    onChange={e => updateDomain(index, { host: e.target.value })}
                    placeholder="github.com"
                  />
                  <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={domain.includeSubpaths !== false}
                      onChange={e =>
                        updateDomain(index, { includeSubpaths: e.target.checked })
                      }
                    />
                    {t('sessions.contextRules.includeSubpaths')}
                  </label>
                  <button
                    type="button"
                    className="p-2 text-muted-foreground hover:text-destructive shrink-0"
                    onClick={() => removeDomain(index)}
                    aria-label={t('common.delete')}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {domainConflicts.length > 0 && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
          {domainConflicts.map(c => (
            <p key={c.host}>
              {t('sessions.contextRules.domainConflict', {
                host: c.host,
                session: c.otherSessionName,
              })}
            </p>
          ))}
        </div>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={value.strictMode !== false}
          onChange={e => onChange({ ...value, strictMode: e.target.checked })}
        />
        {t('sessions.contextRules.strictMode')}
      </label>
    </div>
  );
}
